import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import { FormControlLabel, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';

import { useDevices } from '@/api/devices';
import { usePostActivateTimefence, usePostDeactivateTimefence } from '@/api/timefence';
import { SensorIcon } from '@/components/data/SensorIcon';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Switch } from '@/components/ui/Switch';
import { useSpotterContext } from '@/context/SpotterContext';
import { useDisclosure } from '@/hooks/useDisclosure';

export const TimefenceSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { spotterId } = useSpotterContext();
    const { mutateAsync: activateDataTimefence } = usePostActivateTimefence();
    const { mutateAsync: deactivateDataTimefence } = usePostDeactivateTimefence();
    const { data: devices, isFetching: isDevicesFetching } = useDevices({
        devices: [{ spotterId: spotterId as string }],
        config: { enabled: !!spotterId },
    });
    const {
        isOpen: isTimefenceSwitchOn,
        toggle: toggleTimefenceSwitch,
        open: switchTimefenceOn,
        close: switchTimefenceOff,
    } = useDisclosure(devices?.[0].timefenceEnabled);

    const handleSwitchClick = async () => {
        if (!spotterId) {
            console.warn('No Spotter selected');
            return;
        }
        if (isTimefenceSwitchOn) {
            await deactivateDataTimefence({ spotterId: spotterId });
            toggleTimefenceSwitch();
            return;
        }
        await activateDataTimefence({ spotterId: spotterId });
        toggleTimefenceSwitch();
    };

    useEffect(() => {
        if (!devices) {
            return;
        }
        const isTimefenceOn = devices[0].timefenceEnabled;
        if (isTimefenceSwitchOn === isTimefenceOn) {
            return;
        }

        if (isTimefenceOn) {
            switchTimefenceOn();
        } else {
            switchTimefenceOff();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices]);

    return (
        <Stack gap={2}>
            <SectionHeader
                isLoading={isDevicesFetching}
                title="Timefence"
                icon={<SensorIcon Icon={AccessAlarmIcon} />}
            />
            <FormControlLabel
                value="start"
                sx={{
                    ml: 0,
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: 5,
                }}
                control={<Switch onClick={handleSwitchClick} checked={isTimefenceSwitchOn} readOnly={readOnly} />}
                label={
                    <Stack>
                        <Typography>Enable timefence notifications</Typography>
                        <Typography variant="minorLabel">
                            Receive an email alert when your Spotter doesn&apos;t send data at the expected update rate.
                        </Typography>
                    </Stack>
                }
                labelPlacement="start"
            />
        </Stack>
    );
};
